class CollectionBuilder extends HTMLElement {
  constructor() {
    super();

    this.storageKey = 'ctr-collection-builder';
    this.endpoints = {
      cart: '/cart/add.js',
      checkout: '/cart/checkout',
    };
    this.items = [];
    this.subscribe = true;
    this.config = {};
    this.shell = null;
    this.panelHome = null;
    this.sheetOpen = false;
    this.closeTimer = null;
    this.desktopQuery = window.matchMedia('(min-width: 992px)');
  }

  connectedCallback() {
    this.config = this.readConfig();
    this.subscribe =
      this.config.defaultSubscribe !== false && this.getAttribute('data-default-subscribe') !== 'false';
    this.cacheElements();
    this.restoreState();
    this.bindEvents();
    this.ensureShell();
    this.render();
    this.emitChange();
    this.onResize = () => {
      this.syncStickyOffset();
      this.syncThumbsScrollbar();
    };
    window.addEventListener('resize', this.onResize);
    window.addEventListener('scroll', this.onResize, { passive: true });
  }

  disconnectedCallback() {
    this.unbindEvents();
    this.teardownShell();
    window.clearTimeout(this.closeTimer);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('scroll', this.onResize);
    document.body.classList.remove(
      'ctr-collection-builder-active',
      'ctr-collection-builder-mobile-bar-visible',
      'ctr-collection-builder-sheet-open',
    );
  }

  cacheElements() {
    this.panel = this.querySelector('[data-builder-panel]');
    this.countEl = this.querySelector('[data-builder-count]');
    this.thumbsEl = this.querySelector('[data-builder-thumbs]');
    this.emptySlot = this.querySelector('[data-builder-empty-slot]');
    this.thumbsScroll = this.querySelector('[data-builder-thumbs-scroll]');
    this.thumbsScrollThumb = this.querySelector('[data-builder-thumbs-scroll-thumb]');
    this.toggleBtn = this.querySelector('[data-builder-toggle]');
    this.toggleInput = this.querySelector('[data-builder-toggle-input]');
    this.purchaseEl = this.querySelector('[data-builder-purchase]');
    this.saveBadge = this.querySelector('[data-builder-save-badge]');
    this.unitCompare = this.querySelector('[data-builder-unit-compare]');
    this.unitCurrent = this.querySelector('[data-builder-unit-current]');
    this.summaryBody = this.querySelector('[data-builder-summary-body]');
    this.summaryRows = this.querySelector('[data-builder-summary-rows]');
    this.totalCompare = this.querySelector('[data-builder-total-compare]');
    this.totalCurrent = this.querySelector('[data-builder-total-current]');
    this.noteEl = this.querySelector('[data-builder-note]');
    this.ctaBtn = this.querySelector('[data-builder-cta]');
    this.progressPill = this.querySelector('[data-builder-progress-pill]');
    this.progressTrack = this.querySelector('[data-builder-progress]');
    this.progressFill = this.querySelector('[data-builder-progress-fill]');
    this.mobileBar = this.querySelector('[data-builder-mobile-bar]');
    this.mobileExpand = this.querySelector('[data-builder-mobile-expand]');
    this.mobileTitle = this.querySelector('[data-builder-mobile-title]');
    this.mobilePrices = this.querySelector('.ctr-collection-builder__mobile-bar-prices');
    this.mobileCompare = this.querySelector('[data-builder-mobile-compare]');
    this.mobileCurrent = this.querySelector('[data-builder-mobile-current]');
    this.mobileSaved = this.querySelector('[data-builder-mobile-saved]');
    this.mobileOpen = this.querySelector('[data-builder-mobile-open]');
    this.mobileProgressPill = this.querySelector('[data-builder-mobile-progress-pill]');
    this.sheet = this.querySelector('[data-builder-sheet]');
    this.sheetPanel = this.querySelector('[data-builder-sheet-panel]');
    this.sheetCloseButtons = [...this.querySelectorAll('[data-builder-sheet-close]')];
    this.panelHome = this.panel?.parentElement || this;
  }

  bindEvents() {
    this.onAdd = (event) => this.handleAdd(event);
    this.onRemove = (event) => this.handleRemove(event);
    this.onToggle = () => this.handleToggle();
    this.onPurchaseClick = (event) => this.handlePurchaseClick(event);
    this.onCta = (event) => this.handleCta(event);
    this.onEmptySlot = () => this.scrollToProducts();
    this.onMobileOpen = () => this.openSheet();
    this.onSheetClose = () => this.closeSheet();
    this.onThumbClick = (event) => this.handleThumbClick(event);
    this.onThumbsScroll = () => this.syncThumbsScrollbar();
    this.onThumbsScrollPointerDown = (event) => this.handleThumbsScrollPointerDown(event);
    this.onBreakpoint = () => this.handleBreakpointChange();
    this.onKeydown = (event) => this.handleKeydown(event);
    this.onSectionLoad = () => {
      this.ensureShell();
      this.render();
      this.emitChange();
    };

    document.addEventListener('collection-builder:add', this.onAdd);
    document.addEventListener('collection-builder:remove', this.onRemove);
    this.onSetSubscribe = (event) => {
      if (typeof event.detail?.subscribe === 'boolean') this.setSubscribe(event.detail.subscribe);
    };
    document.addEventListener('collection-builder:set-subscribe', this.onSetSubscribe);
    this.toggleInput?.addEventListener('change', this.onToggle);
    this.purchaseEl?.addEventListener('click', this.onPurchaseClick);
    this.ctaBtn?.addEventListener('click', this.onCta);
    this.emptySlot?.addEventListener('click', this.onEmptySlot);
    this.mobileOpen?.addEventListener('click', this.onMobileOpen);
    this.mobileExpand?.addEventListener('click', this.onMobileOpen);
    this.sheetCloseButtons.forEach((button) => button.addEventListener('click', this.onSheetClose));
    this.thumbsEl?.addEventListener('click', this.onThumbClick);
    this.thumbsEl?.addEventListener('scroll', this.onThumbsScroll, { passive: true });
    this.thumbsScroll?.addEventListener('pointerdown', this.onThumbsScrollPointerDown);
    this.desktopQuery.addEventListener('change', this.onBreakpoint);
    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('shopify:section:load', this.onSectionLoad);
  }

  unbindEvents() {
    document.removeEventListener('collection-builder:add', this.onAdd);
    document.removeEventListener('collection-builder:remove', this.onRemove);
    document.removeEventListener('collection-builder:set-subscribe', this.onSetSubscribe);
    this.toggleInput?.removeEventListener('change', this.onToggle);
    this.purchaseEl?.removeEventListener('click', this.onPurchaseClick);
    this.ctaBtn?.removeEventListener('click', this.onCta);
    this.emptySlot?.removeEventListener('click', this.onEmptySlot);
    this.mobileOpen?.removeEventListener('click', this.onMobileOpen);
    this.mobileExpand?.removeEventListener('click', this.onMobileOpen);
    this.sheetCloseButtons?.forEach((button) => button.removeEventListener('click', this.onSheetClose));
    this.thumbsEl?.removeEventListener('click', this.onThumbClick);
    this.thumbsEl?.removeEventListener('scroll', this.onThumbsScroll);
    this.thumbsScroll?.removeEventListener('pointerdown', this.onThumbsScrollPointerDown);
    this.desktopQuery.removeEventListener('change', this.onBreakpoint);
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('shopify:section:load', this.onSectionLoad);
  }

  readConfig() {
    const node = this.querySelector('[data-collection-builder-config]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error('collection builder config parse failed', error);
      return {};
    }
  }

  restoreState() {
    try {
      const raw = sessionStorage.getItem(this.storageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed.items)) {
        this.items = parsed.items.map((item) => this.normalizeItem(item));
      }
      if (typeof parsed.subscribe === 'boolean') this.subscribe = parsed.subscribe;
    } catch (error) {
      console.error('collection builder restore failed', error);
    }
  }

  normalizeItem(item) {
    if (!item || typeof item !== 'object') return item;
    return {
      ...item,
      categoryId: item.categoryId != null ? String(item.categoryId) : '',
      otpCents: Number(item.otpCents) || 0,
      subCents: Number(item.subCents) || 0,
      discountTiers: this.parseTiers(item.discountTiers),
      freeGifts: Array.isArray(item.freeGifts) ? item.freeGifts : [],
      preorder: Boolean(item.preorder),
      preorderShipDate: item.preorderShipDate || '',
    };
  }

  persistState() {
    try {
      sessionStorage.setItem(
        this.storageKey,
        JSON.stringify({
          items: this.items,
          subscribe: this.subscribe,
        }),
      );
    } catch (error) {
      console.error('collection builder persist failed', error);
    }
  }

  ensureShell() {
    const section = this.closest('.shopify-section');
    if (!section) return;

    const groups = [...document.querySelectorAll('[data-collection-group]')];
    const groupSections = groups
      .map((group) => group.closest('.shopify-section'))
      .filter(Boolean);

    if (!groupSections.length) return;

    if (this.shell && document.body.contains(this.shell)) {
      this.syncStickyOffset();
      this.placePanel();
      return;
    }

    const wrap = document.createElement('div');
    wrap.className = 'ctr-collection-builder-shell-wrap';
    const shell = document.createElement('div');
    shell.className = 'ctr-collection-builder-shell';
    const main = document.createElement('div');
    main.className = 'ctr-collection-builder-shell__main';
    const aside = document.createElement('div');
    aside.className = 'ctr-collection-builder-shell__aside';

    const first = groupSections[0];
    first.parentNode.insertBefore(wrap, first);
    wrap.appendChild(shell);
    shell.append(main, aside);

    for (const groupSection of groupSections) {
      main.appendChild(groupSection);
    }

    this.shellWrap = wrap;
    this.shell = shell;
    this.shellMain = main;
    this.shellAside = aside;
    section.classList.add('is-shelled');
    this.syncStickyOffset();
    this.placePanel();
  }

  syncStickyOffset() {
    if (!this.shell) return;
    const tabsSection = document.querySelector('.ctr-collection-tabs-section, collection-tabs')?.closest(
      '.shopify-section',
    );
    const fromTabs = tabsSection
      ? getComputedStyle(tabsSection).getPropertyValue('--ctr-collection-tabs-top').trim()
      : '';
    if (fromTabs) {
      this.shell.style.setProperty('--ctr-collection-tabs-top', fromTabs);
      return;
    }

    const header = document.querySelector('.ctr-header-section, .header, sticky-header');
    const offset = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
    this.shell.style.setProperty('--ctr-collection-tabs-top', `${offset}px`);
  }

  placePanel() {
    if (!this.panel || !this.shellAside) return;

    if (this.desktopQuery.matches) {
      this.movePanelTo(this.shellAside);
      if (this.sheetOpen || this.sheet?.classList.contains('is-open')) {
        this.closeSheet({ immediate: true });
      }
    } else if (this.sheetOpen && this.sheetPanel) {
      this.movePanelTo(this.sheetPanel);
    } else {
      this.movePanelTo(this.panelHome);
    }
  }

  movePanelTo(target) {
    if (!this.panel || !target) return;
    if (this.panel.parentElement === target) return;
    target.appendChild(this.panel);
  }

  teardownShell() {
    if (!this.shell) return;

    if (this.panel && this.panelHome && this.panel.parentElement !== this.panelHome) {
      this.panelHome.appendChild(this.panel);
    }

    const anchor = this.shellWrap || this.shell;
    if (this.shellMain) {
      while (this.shellMain.firstChild) {
        anchor.parentNode.insertBefore(this.shellMain.firstChild, anchor);
      }
    }

    anchor.remove();
    this.shellWrap = null;
    this.shell = null;
    this.shellMain = null;
    this.shellAside = null;
    this.closest('.shopify-section')?.classList.remove('is-shelled');
  }

  handleBreakpointChange() {
    this.placePanel();
    this.render();
  }

  handleKeydown(event) {
    if (event.key !== 'Escape' || !this.sheetOpen) return;
    this.closeSheet();
  }

  handleAdd(event) {
    const detail = event.detail;
    if (!detail?.variantId) return;

    const categoryId = detail.categoryId || '';
    const category = this.readCategoryById(categoryId) || {};

    this.items.push(
      this.normalizeItem({
        key: `${detail.variantId}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        variantId: Number(detail.variantId),
        productId: Number(detail.productId) || null,
        title: detail.title || '',
        image: detail.image || '',
        categoryId,
        otpCents: this.parseMoneyToCents(
          detail.otpPrice || category.otpPrice,
          Number(detail.otpCents) || category.otpCents || 0,
        ),
        subCents: this.parseMoneyToCents(
          detail.subPrice || category.subPrice,
          Number(detail.subCents) || category.subCents || 0,
        ),
        sellingPlanId: detail.sellingPlanId || category.sellingPlanId || '',
        offerPropertyEnabled: Boolean(detail.offerPropertyEnabled || category.offerPropertyEnabled),
        offerPropertyValue: detail.offerPropertyValue || category.offerPropertyValue || '',
        freeGifts: Array.isArray(detail.freeGifts)
          ? detail.freeGifts
          : category.freeGifts || [],
        discountTiers:
          category.discountTiers?.length > 0 ? category.discountTiers : detail.discountTiers,
        preorder: Boolean(detail.preorder),
        preorderShipDate: detail.preorderShipDate || '',
      }),
    );

    this.persistState();
    this.render();
    this.emitChange();
  }

  handleRemove(event) {
    const variantId = Number(event.detail?.variantId);
    if (!variantId) return;

    let removeIndex = -1;
    for (let i = this.items.length - 1; i >= 0; i -= 1) {
      if (Number(this.items[i].variantId) === variantId) {
        removeIndex = i;
        break;
      }
    }
    if (removeIndex < 0) return;

    this.items.splice(removeIndex, 1);
    this.persistState();
    this.render();
    this.emitChange();
  }

  handlePurchaseClick(event) {
    if (!this.toggleInput) return;
    // Let the native checkbox handle clicks on the switch itself (same as routine-addon)
    if (event.target.closest('[data-builder-toggle]')) return;
    this.toggleInput.checked = !this.toggleInput.checked;
    this.handleToggle();
  }

  handleToggle() {
    this.subscribe = this.toggleInput ? Boolean(this.toggleInput.checked) : !this.subscribe;
    // Sync visual class before heavy render so transition can start uninterrupted
    this.renderToggle();
    this.persistState();
    this.render();
    this.emitChange();
  }

  setSubscribe(value) {
    const next = Boolean(value);
    if (this.subscribe === next) {
      if (this.toggleInput) {
        this.toggleInput.checked = next;
        this.toggleInput.setAttribute('aria-checked', next ? 'true' : 'false');
      }
      this.renderToggle();
      return;
    }
    this.subscribe = next;
    if (this.toggleInput) {
      this.toggleInput.checked = next;
      this.toggleInput.setAttribute('aria-checked', next ? 'true' : 'false');
    }
    this.renderToggle();
    this.persistState();
    this.render();
    this.emitChange();
  }

  getQuickShopUpsell(categoryId) {
    const category = this.readCategoryById(categoryId);
    if (!category) return null;

    const sample = {
      key: 'qs-temp',
      categoryId: String(categoryId || ''),
      otpCents: category.otpCents || 0,
      subCents: category.subCents || category.otpCents || 0,
      discountTiers: category.discountTiers || [],
      freeGifts: Array.isArray(category.freeGifts) ? category.freeGifts : [],
      isFreeGift: false,
    };

    const previousItems = this.items;
    this.items = [...previousItems.filter((item) => !item.isFreeGift), sample];
    const pricing = this.getPricing();
    const bump = this.getNextSavingsBump(pricing);
    this.items = previousItems;
    return bump;
  }

  handleThumbClick(event) {
    const removeBtn = event.target.closest('[data-builder-remove]');
    if (!removeBtn) return;
    const key = removeBtn.getAttribute('data-builder-remove');
    this.items = this.items.filter((item) => item.key !== key);
    this.persistState();
    this.render();
    this.emitChange();
  }

  emitChange() {
    document.dispatchEvent(
      new CustomEvent('collection-builder:change', {
        detail: {
          items: this.items,
          subscribe: this.subscribe,
        },
      }),
    );
  }

  scrollToProducts() {
    const group = document.querySelector('[data-collection-group]:not([hidden])');
    const target = group?.closest('.shopify-section') || group;
    if (!target) return;
    const top = target.getBoundingClientRect().top + window.scrollY - 120;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    this.closeSheet();
  }

  openSheet() {
    if (!this.sheet || !this.sheetPanel || !this.panel || this.desktopQuery.matches) return;
    if (this.sheetOpen) return;

    window.clearTimeout(this.closeTimer);
    this.sheetOpen = true;
    this.sheetPanel.appendChild(this.panel);
    this.sheet.hidden = false;
    this.sheet.style.removeProperty('display');
    this.sheet.setAttribute('aria-hidden', 'false');
    this.mobileBar?.classList.add('is-sheet-open');
    this.mobileExpand?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('ctr-collection-builder-sheet-open');

    this.sheet.classList.add('is-closing');
    void this.sheet.offsetWidth;
    this.sheet.classList.remove('is-closing');
    this.sheet.classList.add('is-open');

    window.requestAnimationFrame(() => {
      this.sheetPanel?.focus?.({ preventScroll: true });
    });
  }

  closeSheet({ immediate = false } = {}) {
    if (!this.sheet) return;

    const finish = () => {
      this.sheetOpen = false;
      this.sheet.classList.remove('is-open', 'is-closing');
      this.sheet.hidden = true;
      this.sheet.style.setProperty('display', 'none', 'important');
      this.sheet.setAttribute('aria-hidden', 'true');
      this.mobileBar?.classList.remove('is-sheet-open');
      this.mobileExpand?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('ctr-collection-builder-sheet-open');

      if (this.desktopQuery.matches) {
        this.movePanelTo(this.shellAside);
      } else {
        this.movePanelTo(this.panelHome);
      }
    };

    if (!this.sheetOpen && !this.sheet.classList.contains('is-open')) {
      // Already closed — don't re-append panel (that cancels CSS transitions)
      this.sheet.classList.remove('is-open', 'is-closing');
      this.sheet.hidden = true;
      this.sheet.style.setProperty('display', 'none', 'important');
      this.sheet.setAttribute('aria-hidden', 'true');
      return;
    }

    window.clearTimeout(this.closeTimer);
    this.sheetOpen = false;
    this.mobileExpand?.setAttribute('aria-expanded', 'false');
    this.mobileBar?.classList.remove('is-sheet-open');
    document.body.classList.remove('ctr-collection-builder-sheet-open');

    if (immediate) {
      finish();
      return;
    }

    this.sheet.classList.add('is-closing');
    this.sheet.classList.remove('is-open');
    this.sheet.setAttribute('aria-hidden', 'true');

    this.closeTimer = window.setTimeout(finish, 300);
  }

  getPricing() {
    const paidItems = this.items.filter((item) => !item.isFreeGift);
    const count = paidItems.length;
    const active = this.computeTotals(paidItems, this.subscribe);
    const subscribeTotals = this.subscribe
      ? active
      : this.computeTotals(paidItems, true);
    const reference = this.getReferenceUnitPrices();

    return {
      count,
      otpTotal: active.otpTotal,
      paidTotal: active.paidTotal,
      discountCents: active.discountCents,
      savePercent: active.savePercent,
      gifts: active.gifts,
      subscribeDiscountCents: subscribeTotals.discountCents,
      subscribeSavePercent: subscribeTotals.savePercent,
      subscribeGifts: subscribeTotals.gifts,
      subscribePaidTotal: subscribeTotals.paidTotal,
      unitCompareCents: count ? active.otpTotal : reference.otpCents,
      unitCurrentCents: count
        ? active.paidTotal
        : this.subscribe
          ? reference.subCents
          : reference.otpCents,
    };
  }

  computeTotals(paidItems, asSubscribe) {
    const categoryCounts = this.getCategoryCounts(paidItems);
    const categoryAmountOff = {};
    const categoryTiers = {};

    for (const [key, categoryCount] of Object.entries(categoryCounts)) {
      const sample = paidItems.find((item) => this.categoryKey(item) === key);
      const category = this.readCategoryById(key);
      const liveTiers = category?.discountTiers || [];
      const itemTiers = this.parseTiers(sample?.discountTiers);
      const tiers = liveTiers.length ? liveTiers : itemTiers;
      categoryTiers[key] = tiers;
      categoryAmountOff[key] = this.resolveTierAmount(tiers, categoryCount);
    }

    let otpTotal = 0;
    let paidTotal = 0;
    const giftMap = new Map();

    for (const item of paidItems) {
      const key = this.categoryKey(item);
      const category = this.readCategoryById(key) || {};
      const otpCents = item.otpCents || category.otpCents || item.subCents || 0;
      const subCents = item.subCents || category.subCents || otpCents;
      const amountOff = categoryAmountOff[key];
      const unitPaid = asSubscribe
        ? this.unitPriceFromAmountOff(otpCents, subCents, amountOff)
        : otpCents;

      otpTotal += otpCents;
      paidTotal += unitPaid;

      if (asSubscribe) {
        const giftsSource =
          Array.isArray(category.freeGifts) && category.freeGifts.length
            ? category.freeGifts
            : item.freeGifts;
        if (Array.isArray(giftsSource)) {
          for (const gift of giftsSource) {
            if (!gift?.id) continue;
            if (!giftMap.has(gift.id)) giftMap.set(gift.id, gift);
          }
        }
      }
    }

    const discountCents = Math.max(0, otpTotal - paidTotal);
    const savePercent = otpTotal > 0 ? Math.round((discountCents / otpTotal) * 100) : 0;

    return {
      otpTotal,
      paidTotal,
      discountCents,
      savePercent,
      gifts: [...giftMap.values()],
      categoryCounts,
      categoryTiers,
    };
  }

  getProgressTarget() {
    const target = Number(this.config.progressTarget);
    return Number.isFinite(target) && target > 0 ? target : 4;
  }

  getProgressInfo(pricing) {
    const count = pricing.count;
    if (count < 1) return null;

    const target = this.getProgressTarget();
    const fill = Math.min(1, count / target);
    const currentDiscount = pricing.subscribeDiscountCents || 0;
    const nextBump = this.getNextSavingsBump(pricing);

    if (count >= target && currentDiscount > 0) {
      return { mode: 'saved', amountCents: currentDiscount, more: 0, fill };
    }

    if (nextBump && nextBump.more > 0 && nextBump.amountCents > 0) {
      return {
        mode: 'add_more',
        more: nextBump.more,
        amountCents: nextBump.amountCents,
        fill,
      };
    }

    if (currentDiscount > 0) {
      return { mode: 'saved', amountCents: currentDiscount, more: 0, fill };
    }

    return { mode: 'bar', amountCents: 0, more: 0, fill };
  }

  getNextSavingsBump(pricing) {
    const paidItems = this.items.filter((item) => !item.isFreeGift);
    if (!paidItems.length) return null;

    const currentDiscount = pricing.subscribeDiscountCents || 0;
    const categoryCounts = this.getCategoryCounts(paidItems);
    let best = null;

    for (const [key, categoryCount] of Object.entries(categoryCounts)) {
      const sample = paidItems.find((item) => this.categoryKey(item) === key);
      const category = this.readCategoryById(key);
      const liveTiers = category?.discountTiers || [];
      const itemTiers = this.parseTiers(sample?.discountTiers);
      const tiers = liveTiers.length ? liveTiers : itemTiers;
      if (!tiers.length) continue;

      const sorted = [...tiers].sort((a, b) => a.minQty - b.minQty);
      const zeroIndexed = sorted.some((tier) => tier.minQty === 0);
      const nextTier = sorted.find((tier) =>
        zeroIndexed ? categoryCount <= tier.minQty : categoryCount < tier.minQty,
      );
      if (!nextTier) continue;

      const nextQty = zeroIndexed ? nextTier.minQty + 1 : nextTier.minQty;
      const more = Math.max(1, nextQty - categoryCount);
      const projected = this.computeTotalsWithExtra(paidItems, key, more);
      const amountCents = Math.max(0, projected.discountCents - currentDiscount);
      if (amountCents <= 0) continue;

      if (!best || more < best.more || (more === best.more && amountCents > best.amountCents)) {
        best = { more, amountCents };
      }
    }

    if (best) return best;

    // Fallback: +1 box in the first category with items
    const firstKey = Object.keys(categoryCounts)[0];
    if (!firstKey) return null;
    const projected = this.computeTotalsWithExtra(paidItems, firstKey, 1);
    const amountCents = Math.max(0, projected.discountCents - currentDiscount);
    if (amountCents <= 0) return null;
    return { more: 1, amountCents };
  }

  computeTotalsWithExtra(paidItems, categoryId, extraCount) {
    if (!extraCount) return this.computeTotals(paidItems, true);
    const sample = paidItems.find((item) => this.categoryKey(item) === String(categoryId));
    if (!sample) return this.computeTotals(paidItems, true);

    const extras = [];
    for (let i = 0; i < extraCount; i += 1) {
      extras.push({ ...sample, key: `${sample.key}-extra-${i}`, isFreeGift: false });
    }
    return this.computeTotals([...paidItems, ...extras], true);
  }

  categoryKey(item) {
    return String(item?.categoryId || '');
  }

  getCategoryCounts(items) {
    const counts = {};
    for (const item of items) {
      const key = this.categoryKey(item);
      counts[key] = (counts[key] || 0) + 1;
    }
    return counts;
  }

  readCategoryById(categoryId) {
    if (categoryId == null || categoryId === '') return null;
    const id = String(categoryId);
    const groups = document.querySelectorAll('[data-collection-group]');
    for (const group of groups) {
      if (String(group.getAttribute('data-collection-category-id') || '') === id) {
        return this.readGroupPricing(group);
      }
    }
    return null;
  }

  unitPriceFromAmountOff(otpCents, subCents, amountOff) {
    if (!otpCents) return subCents || 0;
    if (amountOff == null || amountOff === '') return subCents || otpCents;
    const offCents = Math.round(parseFloat(amountOff) * 100);
    if (!Number.isFinite(offCents)) return subCents || otpCents;
    return Math.max(0, otpCents - offCents);
  }

  unitPriceForItem(item, categoryCount) {
    const otpCents = item.otpCents || 0;
    const subCents = item.subCents > 0 ? item.subCents : otpCents;
    const tiers = item.discountTiers || [];
    const amountOff = tiers.length ? this.resolveTierAmount(tiers, categoryCount) : null;
    return this.unitPriceFromAmountOff(otpCents, subCents, amountOff);
  }

  resolveTierAmount(tiers, count) {
    if (!Array.isArray(tiers) || !tiers.length || !count) return null;
    const sorted = [...tiers].sort((a, b) => a.minQty - b.minQty);
    // Support 0-indexed (0:16 / 1:20) and 1-indexed (1:16 / 2:20) configs
    const zeroIndexed = sorted.some((tier) => tier.minQty === 0);
    let matched = null;
    for (const tier of sorted) {
      const hits = zeroIndexed ? count > tier.minQty : count >= tier.minQty;
      if (hits) matched = tier.amount ?? tier.pct;
    }
    return matched;
  }

  getReferenceUnitPrices() {
    const paidItems = this.items.filter((item) => !item.isFreeGift);
    if (paidItems.length) {
      const first = paidItems[0];
      const categoryCount = paidItems.filter(
        (item) => this.categoryKey(item) === this.categoryKey(first),
      ).length;
      return {
        otpCents: first.otpCents || 0,
        subCents: this.unitPriceForItem(first, Math.max(1, categoryCount)),
      };
    }

    const group =
      document.querySelector('[data-collection-group]:not([hidden])') ||
      document.querySelector('[data-collection-group]');
    const pricing = this.readGroupPricing(group);
    return {
      otpCents: pricing.otpCents || 0,
      subCents: this.unitPriceForItem(
        {
          otpCents: pricing.otpCents || 0,
          subCents: pricing.subCents || pricing.otpCents || 0,
          discountTiers: pricing.discountTiers || [],
        },
        1,
      ),
    };
  }

  readGroupPricing(group) {
    if (!group) return { otpCents: 0, subCents: 0, discountTiers: [] };
    const node = group.querySelector('[data-collection-builder-category]');
    if (!node) return { otpCents: 0, subCents: 0, discountTiers: [] };
    try {
      const parsed = JSON.parse(node.textContent);
      return {
        ...parsed,
        otpCents: this.parseMoneyToCents(parsed.otpPrice, Number(parsed.otpCents) || 0),
        subCents: this.parseMoneyToCents(parsed.subPrice, Number(parsed.subCents) || 0),
        discountTiers: this.parseTiers(parsed.discountTiers),
        freeGifts: Array.isArray(parsed.freeGifts) ? parsed.freeGifts : [],
      };
    } catch (error) {
      return { otpCents: 0, subCents: 0, discountTiers: [] };
    }
  }

  parseTiers(value) {
    if (Array.isArray(value)) {
      return value
        .map((tier) => ({
          minQty: Number(tier.minQty),
          amount: Number(tier.amount ?? tier.pct),
        }))
        .filter((tier) => Number.isFinite(tier.minQty) && Number.isFinite(tier.amount));
    }

    if (!value || typeof value !== 'string') return [];

    const lines = value
      .split(/\n|;/)
      .map((line) => line.trim())
      .filter(Boolean);

    const tiers = [];
    let autoQty = 1;

    for (const line of lines) {
      if (line.includes(':')) {
        const colon = line.indexOf(':');
        const minQty = Number(String(line.slice(0, colon)).trim());
        const amount = Number(
          String(line.slice(colon + 1))
            .trim()
            .replace(',', '.'),
        );
        if (Number.isFinite(minQty) && Number.isFinite(amount)) {
          tiers.push({ minQty, amount });
        }
        continue;
      }

      // Bare amounts: "15" then "19.50" → 1:15, 2:19.50
      const amount = Number(String(line).replace(',', '.'));
      if (Number.isFinite(amount)) {
        tiers.push({ minQty: autoQty, amount });
        autoQty += 1;
      }
    }

    return tiers;
  }

  parseMoneyToCents(value, fallback = 0) {
    if (value == null || value === '') return fallback;
    if (typeof value === 'number') return Math.round(value);
    const cleaned = String(value).replace(/[^0-9.]/g, '');
    if (!cleaned) return fallback;
    return Math.round(parseFloat(cleaned) * 100);
  }

  render() {
    const pricing = this.getPricing();
    this.renderToggle();
    this.renderCount(pricing.count);
    this.renderThumbs();
    this.renderUnitPrices(pricing);
    this.renderSaveBadge(pricing);
    this.renderSummary(pricing);
    this.renderCta(pricing);
    this.renderProgress(pricing);
    this.renderMobileBar(pricing);
    this.placePanel();
  }

  renderToggle() {
    if (this.toggleBtn) this.toggleBtn.classList.toggle('is-on', this.subscribe);
    if (this.toggleInput) {
      this.toggleInput.checked = this.subscribe;
      this.toggleInput.setAttribute('aria-checked', this.subscribe ? 'true' : 'false');
    }
  }

  renderCount(count) {
    if (this.countEl) this.countEl.textContent = `(${count})`;
  }

  renderThumbs() {
    if (!this.thumbsEl || !this.emptySlot) return;

    this.thumbsEl.querySelectorAll('[data-builder-thumb]').forEach((node) => node.remove());

    for (const item of this.items) {
      const thumb = document.createElement('div');
      thumb.className = 'ctr-collection-builder__thumb';
      thumb.setAttribute('data-builder-thumb', item.key);
      thumb.innerHTML = `
        ${item.image ? `<img src="${item.image}" alt="${this.escapeHtml(item.title)}" width="100" height="100" loading="lazy">` : ''}
        <button type="button" class="ctr-collection-builder__thumb-remove" data-builder-remove="${item.key}" aria-label="Remove ${this.escapeHtml(item.title)}">×</button>
      `;
      this.thumbsEl.insertBefore(thumb, this.emptySlot);
    }

    window.requestAnimationFrame(() => this.syncThumbsScrollbar());
  }

  syncThumbsScrollbar() {
    if (!this.thumbsEl || !this.thumbsScroll || !this.thumbsScrollThumb) return;

    const { scrollWidth, clientWidth, scrollLeft } = this.thumbsEl;
    const overflow = scrollWidth - clientWidth > 1;

    this.thumbsScroll.hidden = !overflow;
    if (!overflow) {
      this.thumbsScrollThumb.style.width = '';
      this.thumbsScrollThumb.style.transform = '';
      return;
    }

    const trackWidth = this.thumbsScroll.clientWidth;
    const thumbWidth = Math.max(24, (clientWidth / scrollWidth) * trackWidth);
    const maxScroll = scrollWidth - clientWidth;
    const maxOffset = Math.max(0, trackWidth - thumbWidth);
    const offset = maxScroll > 0 ? (scrollLeft / maxScroll) * maxOffset : 0;

    this.thumbsScrollThumb.style.width = `${thumbWidth}px`;
    this.thumbsScrollThumb.style.transform = `translateX(${offset}px)`;
  }

  handleThumbsScrollPointerDown(event) {
    if (!this.thumbsEl || !this.thumbsScroll || !this.thumbsScrollThumb) return;
    if (this.thumbsScroll.hidden) return;

    event.preventDefault();
    const trackRect = this.thumbsScroll.getBoundingClientRect();
    const thumbWidth = this.thumbsScrollThumb.offsetWidth;
    const maxOffset = Math.max(0, trackRect.width - thumbWidth);
    const maxScroll = this.thumbsEl.scrollWidth - this.thumbsEl.clientWidth;

    const scrollFromClientX = (clientX) => {
      const x = clientX - trackRect.left - thumbWidth / 2;
      const ratio = maxOffset > 0 ? Math.min(1, Math.max(0, x / maxOffset)) : 0;
      this.thumbsEl.scrollLeft = ratio * maxScroll;
      this.syncThumbsScrollbar();
    };

    scrollFromClientX(event.clientX);

    const onMove = (moveEvent) => scrollFromClientX(moveEvent.clientX);
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }

  renderUnitPrices(pricing) {
    // Cart-level compare/current (same basis as save % and summary totals)
    if (this.unitCompare) {
      this.unitCompare.textContent = this.formatMoney(pricing.unitCompareCents);
      this.unitCompare.hidden =
        !pricing.unitCompareCents || pricing.unitCompareCents <= pricing.unitCurrentCents;
    }
    if (this.unitCurrent) {
      this.unitCurrent.textContent = this.formatMoney(pricing.unitCurrentCents);
    }
  }

  renderSaveBadge(pricing) {
    if (!this.saveBadge) return;
    const percent = this.subscribe ? pricing.savePercent : pricing.subscribeSavePercent;
    if (!pricing.count || !percent) {
      this.saveBadge.hidden = true;
      this.saveBadge.classList.remove('is-missed');
      return;
    }
    const template = this.config.saveBadgeTemplate || 'Save {percent}%';
    this.saveBadge.textContent = template.replace('{percent}', String(percent));
    this.saveBadge.hidden = false;
    this.saveBadge.classList.toggle('is-missed', !this.subscribe);
  }

  renderSummary(pricing) {
    if (!this.summaryBody || !this.summaryRows) return;

    if (!pricing.count) {
      this.summaryBody.hidden = true;
      this.summaryRows.innerHTML = '';
      return;
    }

    this.summaryBody.hidden = false;
    const rows = [];
    const fullLabelTemplate = this.config.fullPriceLabel || '{count} boxes at full price';
    let fullLabel = fullLabelTemplate.replace('{count}', String(pricing.count));
    if (pricing.count === 1) {
      fullLabel = fullLabel.replace(/\bboxes\b/i, 'box');
    }
    rows.push(this.summaryRow(fullLabel, this.formatMoney(pricing.otpTotal), true));

    const displayDiscount = this.subscribe
      ? pricing.discountCents
      : pricing.subscribeDiscountCents;
    const displayGifts = this.subscribe ? pricing.gifts : pricing.subscribeGifts;
    const missed = !this.subscribe;

    if (displayDiscount > 0) {
      rows.push(
        this.summaryRow(
          this.config.discountLabel || 'Subscribe & Save discount',
          `−${this.formatMoney(displayDiscount)}`,
          false,
          true,
          missed,
        ),
      );
    }

    for (const gift of displayGifts || []) {
      rows.push(
        this.summaryRow(
          gift.title || this.config.freeGiftFallbackLabel || 'Free gift',
          'FREE',
          false,
          true,
          missed,
        ),
      );
    }

    this.summaryRows.innerHTML = rows.join('');

    if (this.totalCompare) {
      this.totalCompare.textContent = this.formatMoney(pricing.otpTotal);
      this.totalCompare.hidden = !this.subscribe || pricing.otpTotal <= pricing.paidTotal;
    }
    if (this.totalCurrent) {
      this.totalCurrent.textContent = this.formatMoney(pricing.paidTotal);
    }
    if (this.noteEl) {
      const template = this.subscribe
        ? this.config.subscribeNote || ''
        : this.config.otpNote || '';
      this.noteEl.textContent = template.replace('{count}', String(pricing.count));
      this.noteEl.hidden = !template;
    }
  }

  summaryRow(label, value, strong = false, savings = false, missed = false) {
    const valueClass = [
      'ctr-collection-builder__summary-row-value',
      savings ? 'is-savings' : '',
      missed ? 'is-missed' : '',
    ]
      .filter(Boolean)
      .join(' ');
    return `
      <div class="ctr-collection-builder__summary-row${missed ? ' is-missed' : ''}">
        <span class="ctr-collection-builder__summary-row-label${strong ? ' is-strong' : ''}${missed ? ' is-missed' : ''}">${this.escapeHtml(label)}</span>
        <span class="${valueClass}">${this.escapeHtml(value)}</span>
      </div>
    `;
  }

  renderCta(pricing) {
    if (!this.ctaBtn) return;
    const empty = pricing.count < 1;
    this.ctaBtn.disabled = empty;
    this.ctaBtn.classList.toggle('is-disabled', empty);
    this.ctaBtn.textContent = empty
      ? this.config.emptyCtaText || 'Add flavor box'
      : this.config.ctaText || 'Checkout';
  }

  renderProgress(pricing) {
    const info = this.getProgressInfo(pricing);
    this.renderProgressPill(this.progressPill, info);
    this.renderProgressPill(this.mobileProgressPill, info);

    // Thin track under thumbs paused — not in PM notes (only Figma open drawer).
    // if (!this.progressTrack || !this.progressFill) return;
    // if (!info || pricing.count < 1) {
    //   this.progressTrack.hidden = true;
    //   this.progressFill.style.width = '0%';
    //   return;
    // }
    // this.progressTrack.hidden = false;
    // this.progressFill.style.width = `${Math.round((info.fill || 0) * 100)}%`;
  }

  renderProgressPill(el, info) {
    if (!el) return;
    if (!info || info.mode === 'bar' || !info.amountCents) {
      el.hidden = true;
      el.textContent = '';
      return;
    }

    if (info.mode === 'saved') {
      const template = this.config.progressSavedTemplate || 'You Saved {amount}!';
      el.textContent = template.replace('{amount}', this.formatMoney(info.amountCents));
    } else {
      const template = this.config.progressAddMoreTemplate || 'Add {n} more, Save {amount}';
      el.textContent = template
        .replace('{n}', String(info.more))
        .replace('{amount}', this.formatMoney(info.amountCents));
    }
    el.hidden = false;
  }

  renderMobileBar(pricing) {
    if (!this.mobileBar) return;

    const showBar = !this.desktopQuery.matches && pricing.count >= 1;

    if (!showBar) {
      this.mobileBar.hidden = true;
      document.body.classList.remove('ctr-collection-builder-mobile-bar-visible');
      if (pricing.count < 1 && this.sheetOpen) {
        this.closeSheet({ immediate: true });
      }
      return;
    }

    this.mobileBar.hidden = false;
    document.body.classList.add(
      'ctr-collection-builder-active',
      'ctr-collection-builder-mobile-bar-visible',
    );
    this.mobileBar.classList.toggle('is-sheet-open', this.sheetOpen);

    const target = this.getProgressTarget();
    if (this.mobileTitle) {
      const template =
        this.config.mobileBarTitle || 'Your Items - {count} / {target} selected';
      this.mobileTitle.textContent = template
        .replace('{count}', String(pricing.count))
        .replace('{target}', String(target));
    }

    if (this.mobilePrices) {
      this.mobilePrices.hidden = pricing.count < 1;
    }

    if (this.mobileCompare) {
      this.mobileCompare.textContent = this.formatMoney(pricing.unitCompareCents);
      this.mobileCompare.hidden =
        !this.subscribe ||
        !pricing.unitCompareCents ||
        pricing.unitCompareCents <= pricing.unitCurrentCents;
    }

    if (this.mobileCurrent) {
      this.mobileCurrent.textContent = this.formatMoney(pricing.unitCurrentCents);
    }

    if (this.mobileSaved) {
      if (this.subscribe && pricing.count && pricing.discountCents > 0) {
        const template = this.config.mobileSavedTemplate || 'Saved {amount} on Subscription';
        this.mobileSaved.textContent = template.replace(
          '{amount}',
          this.formatMoney(pricing.discountCents),
        );
        this.mobileSaved.hidden = false;
      } else {
        this.mobileSaved.hidden = true;
      }
    }

    if (this.mobileOpen) {
      this.mobileOpen.textContent = this.config.mobileOpenText || 'Your items';
    }

    this.mobileExpand?.setAttribute('aria-expanded', this.sheetOpen ? 'true' : 'false');
  }

  async handleCta(event) {
    if (event) event.preventDefault();
    if (!this.items.length || this.ctaBtn?.disabled) {
      this.scrollToProducts();
      return;
    }

    const oldText = this.ctaBtn?.innerText;
    try {
      this.ctaBtn?.classList.add('is-loading');
      this.ctaBtn?.setAttribute('disabled', 'true');
      if (this.ctaBtn) this.ctaBtn.innerText = this.config.loadingText || 'Mixing Your Magna...';

      const $cartDrawer = document.querySelector('cart-drawer');
      const sections = $cartDrawer ? $cartDrawer.getSectionsToRender().map((section) => section.id) : [];
      let items = this.buildCartItems();

      if (this.addToCartType === 'checkout' && typeof window.clearCart === 'function') {
        await window.clearCart();
      }

      items = await this.appendFreeGifts(items);

      const response = await fetch(this.endpoints.cart, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items, sections }),
      });

      if (!response.ok) throw new Error('There was a problem with the request');

      if (window.location.host.includes('drinkmagna') && window.dataLayer) {
        window.dataLayer.push({ event: 'add_to_cart', items });
      }

      const json = await response.json();

      this.items = [];
      this.persistState();
      this.render();
      this.emitChange();
      this.closeSheet();

      if (this.addToCartType === 'drawer' && $cartDrawer) {
        $cartDrawer.renderContents(json);
      } else {
        document.location.href = this.endpoints.checkout;
      }
    } catch (error) {
      console.error(error.message);
    } finally {
      if (this.ctaBtn) {
        this.ctaBtn.innerText = oldText;
        this.ctaBtn.classList.remove('is-loading');
        if (this.items.length) this.ctaBtn.removeAttribute('disabled');
      }
    }
  }

  buildCartItems() {
    return this.items.map((item) => {
      const line = {
        id: Number(item.variantId),
        quantity: 1,
      };

      if (this.subscribe && item.sellingPlanId) {
        line.selling_plan = Number(item.sellingPlanId) || item.sellingPlanId;
      }

      const properties = {};
      if (
        this.subscribe &&
        item.offerPropertyEnabled &&
        item.offerPropertyValue &&
        item.sellingPlanId
      ) {
        properties._offer = item.offerPropertyValue;
      }

      if (item.preorder) {
        properties.PREORDER = this.formatPreorderProperty(item.preorderShipDate);
      }

      if (Object.keys(properties).length) {
        line.properties = properties;
      }

      return line;
    });
  }

  formatPreorderProperty(rawShipDate) {
    const raw = String(rawShipDate || '').trim();
    if (!raw) return 'PREORDER';
    const parts = raw.split(/\s+/);
    const day = parts[parts.length - 1];
    const dayNum = parseInt(day, 10);
    if (!Number.isNaN(dayNum)) {
      parts[parts.length - 1] = `${dayNum}${this.getOrdinal(dayNum)}`;
    }
    return parts.join(' ').toUpperCase();
  }

  getOrdinal(day) {
    const n = parseInt(day, 10);
    if (n > 3 && n < 21) return 'th';
    switch (n % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  }

  async appendFreeGifts(items) {
    if (!this.subscribe) return items;

    const giftIds = [];
    for (const item of this.items) {
      for (const gift of item.freeGifts || []) {
        if (gift?.id && !giftIds.includes(Number(gift.id))) {
          giftIds.push(Number(gift.id));
        }
      }
    }
    if (!giftIds.length) return items;

    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();
    const existingIds = new Set(cart.items.map((item) => item.id));

    for (const productId of giftIds) {
      if (existingIds.has(productId)) continue;
      items.push({
        id: productId,
        quantity: 1,
        properties: { _offer: 'FREE GIFT' },
      });
    }

    return items;
  }

  get addToCartType() {
    return this.getAttribute('data-add-to-cart-type') || this.config.addToCartType || 'drawer';
  }

  formatMoney(cents) {
    const value = Number(cents) || 0;
    const dollars = value / 100;
    if (Number.isInteger(dollars)) return `$${dollars}`;
    return `$${dollars.toFixed(2)}`;
  }

  escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

if (!customElements.get('collection-builder')) {
  customElements.define('collection-builder', CollectionBuilder);
}

window.CollectionBuilder = CollectionBuilder;
